import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import alertify from 'alertifyjs';
import { ApiServiceService } from '../../modules/services/api-service.service';
import { AuthenticationService } from '../../modules/services/authentication.service';
import { TOTPSetupStartResponse } from '../../modules/services/utility-service.models';
import { PageHeaderComponent } from '../page-header/page-header.component';
import { GenericResponse } from '../../modules/shared/models/general.modal';
import { HttpErrorResponse } from '@angular/common/http';

export type UserProfileViewModel = {
  name: string;
  email: string;
  roleLabel: string;
  mda: string;
  phoneNumber: string;
  dateRegistered: string;
  lastLoginText: string;
  accountStatusTitle: string;
  accountStatusSubtitle: string;
};

@Component({
  selector: 'app-user-profile-modal',
  standalone: true,
  imports: [CommonModule, FormsModule, PageHeaderComponent],
  templateUrl: './user-profile-modal.component.html',
  styleUrl: './user-profile-modal.component.scss',
})
export class UserProfileModalComponent implements OnChanges, OnInit {
  @Input({ required: true }) open = false;
  // @Input({ required: true }) profile: UserProfileViewModel | null = null;
  @Output() close = new EventEmitter<void>();
  profile: UserProfileViewModel | null = null;

  setupData: TOTPSetupStartResponse | null = null;
  setupCode = '';
  disableCode = '';
  showDisableCode = false;
  isStartingTOTP = false;
  isConfirmingTOTP = false;
  isDisablingTOTP = false;
  isLoadingProfile = false;

  constructor(
    private readonly apiService: ApiServiceService,
    private readonly authenticationService: AuthenticationService,
  ) {
    alertify.set('notifier', 'position', 'top-right');
    alertify.set('notifier', 'delay', 4);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['open'] && !this.open) {
      this.resetTOTPForms();
    }
  }

  ngOnInit(): void {
    this.fetchUserInfo();
  }

  get isTOTPEnabled(): boolean {
    return this.authenticationService.isTotpEnabled();
  }

  get isTOTPBusy(): boolean {
    return this.isStartingTOTP || this.isConfirmingTOTP || this.isDisablingTOTP;
  }

  get isTOTPSetupPending(): boolean {
    return !!this.setupData;
  }

  get setupQRCodeSrc(): string {
    if (!this.setupData?.qr_code_base64) return '';
    return `data:image/png;base64,${this.setupData.qr_code_base64}`;
  }

  get setupCodeReady(): boolean {
    return this.setupCode.trim().length === 6;
  }

  get disableCodeReady(): boolean {
    return this.disableCode.trim().length === 6;
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (!this.open) return;
    this.close.emit();
  }

  fetchUserInfo() {
    this.isLoadingProfile = true;
    this.apiService.get("/me").subscribe({
      next: (response: GenericResponse<any>) => {
        if (response.status !== "success") {
          alertify.error(response.message || "Failed to get user information");
        } else {
          const data = response.data;
          
          let roleLabel: string;
          
          if (this.authenticationService.hasAnyRoles(["ADMIN"])) {
            roleLabel = "ADMIN"
          } else if (this.authenticationService.hasAnyRoles(["MDA_ADMIN"])) {
            roleLabel = "MDA ADMIN"
          } else {
            roleLabel = "USER"
          }

          this.profile = {
            mda: data.mda_name,
            email: data.email,
            name: data.full_name,
            phoneNumber: data.phone_number,
            roleLabel,
            dateRegistered: data.created_at,
            accountStatusSubtitle: `Account ${data.is_active ? "Active" : "Inactive"}`,
            accountStatusTitle: data.is_active ? "Your account is in good standing" : "Your account is in bad standing",
            lastLoginText: ""
          }
        }
        this.isLoadingProfile = false;
      }, 
      error: (error) => {
        if (error instanceof HttpErrorResponse) {
          alertify.error(error.message || "Failed to get user information");
        } else {
          alertify.error(JSON.stringify(error) || "Failed to get user information");
        }
        this.isLoadingProfile = false;
      }
    })
  }

  onTOTPToggle(event: Event): void {
    const checked = (event.target as HTMLInputElement | null)?.checked ?? false;

    if (checked) {
      if (this.isTOTPEnabled || this.isTOTPSetupPending) return;
      this.startTOTPSetup();
      return;
    }

    if (this.isTOTPSetupPending) {
      this.cancelTOTPSetup();
      return;
    }

    if (this.isTOTPEnabled) {
      this.showDisableCode = true;
    }
  }

  startTOTPSetup(): void {
    this.isStartingTOTP = true;
    this.showDisableCode = false;
    this.disableCode = '';

    this.apiService.startTOTPSetup().subscribe({
      next: (response) => {
        this.isStartingTOTP = false;
        this.setupData = response.data;
        this.setupCode = '';
        alertify.success(response.message || 'TOTP setup started.');
      },
      error: (error) => {
        this.isStartingTOTP = false;
        alertify.error(
          this.readableError(error, 'Unable to start TOTP setup.'),
        );
      },
    });
  }

  confirmTOTPSetup(): void {
    if (!this.setupCodeReady) {
      alertify.error('Enter the 6-digit code from your authenticator app.');
      return;
    }

    this.isConfirmingTOTP = true;

    this.apiService.confirmTOTPSetup(this.setupCode.trim()).subscribe({
      next: (response) => {
        this.isConfirmingTOTP = false;
        this.authenticationService.setTotpEnabled(true);
        this.resetTOTPForms();
        alertify.success(response.message || 'TOTP enabled successfully.');
      },
      error: (error) => {
        this.isConfirmingTOTP = false;
        alertify.error(this.readableError(error, 'Invalid TOTP code.'));
      },
    });
  }

  confirmTOTPDisable(): void {
    if (!this.disableCodeReady) {
      alertify.error('Enter the 6-digit code from your authenticator app.');
      return;
    }

    this.isDisablingTOTP = true;

    this.apiService.disableTOTP(this.disableCode.trim()).subscribe({
      next: (response) => {
        this.isDisablingTOTP = false;
        this.authenticationService.setTotpEnabled(false);
        this.resetTOTPForms();
        alertify.success(response.message || 'TOTP disabled successfully.');
      },
      error: (error) => {
        this.isDisablingTOTP = false;
        alertify.error(this.readableError(error, 'Unable to disable TOTP.'));
      },
    });
  }

  cancelTOTPSetup(): void {
    this.setupData = null;
    this.setupCode = '';
  }

  cancelTOTPDisable(): void {
    this.showDisableCode = false;
    this.disableCode = '';
  }

  normalizeCode(value: string): string {
    return value.replace(/\D/g, '').slice(0, 6);
  }

  copyManualEntryKey(): void {
    if (!this.setupData?.manual_entry_key) return;

    void navigator.clipboard
      .writeText(this.setupData.manual_entry_key)
      .then(() => alertify.success('Manual key copied.'))
      .catch(() => alertify.error('Unable to copy manual key.'));
  }

  private resetTOTPForms(): void {
    this.setupData = null;
    this.setupCode = '';
    this.disableCode = '';
    this.showDisableCode = false;
  }

  private readableError(error: unknown, fallback: string): string {
    return typeof error === 'string' && error.trim() ? error : fallback;
  }
}
